"use client";

/**
 * `LiveRateSubflow` — buyer-side rate picker for live-rate (Shippo) checkouts.
 *
 * Layer: Container (`react-primitive-design §1`). The address is owned by the
 * parent `CheckoutForm`'s `ShippingForm`; this subflow ONLY picks a carrier
 * rate. The previous two-step (address → rates) collected the address again —
 * a UX duplicate AND the inner `<form>` lived inside `CheckoutForm`'s outer
 * `<form>`, so the "Get rates" submit silently triggered the outer onSubmit
 * and closed the cart. Removed entirely.
 *
 * On mount the subflow fires `fetchRates(intent)`. Any change to `sellerId`,
 * `address`, or `parcel` re-fetches via the same race-safe hook (latest-wins
 * + AbortController) — when the buyer edits the upstream `ShippingForm`, the
 * rate list updates without operator intervention.
 *
 * @see .tondocs/shippo/PLAN.md §B step 2-3 — address-first re-sequence
 * @see .tondocs/shippo/PLAN.md §L — rate is ESTIMATE
 * @see .tondocs/skills/react-state-design.md §10 — race-safe async
 * @see .tondocs/skills/concurrency-design.md §7 — ack-once / single-fire
 * @see hooks/use-shipping-rates.ts — Behavior layer
 * @see components/ui/rate-picker.tsx — Primitive layer
 */

import * as React from "react";
import { useTranslations } from "next-intl";
import type { ShippingRatesParcel, ShippingRatesQuote } from "@kraahl/contract";

import { Button } from "@/components/ui/button";
import { RatePicker, type RatePickerDisplay } from "@/components/ui/rate-picker";
import {
  useShippingRates,
  type ShippingRateSelection,
  type ShippingRatesErrorCode,
  type ShippingRatesState,
} from "@/hooks/use-shipping-rates";
import type { Rate } from "@/lib/shipping/types";
import { unreachable } from "@/lib/shipping/label-state";
import type { ShippingRateIntent } from "@/lib/checkout/build-shipping-rates-request";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface LiveRateSubflowProps {
  /** Seller's `profiles.id` — single-seller invariant (§J.5). */
  sellerId: string;
  /** Combined parcel for this seller's items. Parent sums + passes. */
  parcel: ShippingRatesParcel;
  /**
   * Buyer's ship-to address, already validated upstream by `ShippingForm`.
   * The subflow does not collect or edit this — when the buyer changes their
   * address upstream, the new value triggers a re-fetch via `useEffect`.
   */
  address: ShippingRateIntent["address"];
  /**
   * Fires EXACTLY ONCE when the buyer confirms a rate. Parent owns what
   * happens next (typically: render PayPal with the selection folded into the
   * checkout intent).
   */
  onComplete: (selection: ShippingRateSelection) => void;
  /** Fired when buyer dismisses the subflow (falls back to legacy). */
  onCancel?: () => void;
  /**
   * Preview callback — fires on every selection change (BEFORE confirm) so
   * the parent can reflect the rate amount in the displayed total. Pure
   * notification; the canonical confirmation is still `onComplete`. Parent
   * MUST NOT reset this to `null` on its own when LiveRateSubflow unmounts
   * (e.g. after `onComplete` → live_rate_ready) — handle reset via cancel /
   * mode transitions instead.
   */
  onSelectionChange?: (selection: ShippingRateSelection | null) => void;
  className?: string;
}

// ---------------------------------------------------------------------------
// Helpers — derive RatePicker display from the hook's FSM
// ---------------------------------------------------------------------------

interface PickerView {
  display: RatePickerDisplay;
  rates: ReadonlyArray<Rate>;
  selectedId: string | null;
  errorMessage: string | undefined;
  selection: ShippingRateSelection | null;
}

/**
 * Adapt the contract `ShippingRatesQuote` (wire shape) to `Rate` (domain shape
 * the primitive expects). At MVP the boundary parse on the route guarantees
 * `currency` is one of `'USD' | 'CAD'`; the wire `id` is Kraahl-opaque (equals
 * the provider rate id in the current Shippo path).
 */
function quoteToRate(q: ShippingRatesQuote): Rate {
  const currency: "USD" | "CAD" = q.currency === "CAD" ? "CAD" : "USD";
  return {
    id: q.id,
    carrier: q.carrier,
    service: q.service,
    amount: q.amount,
    currency,
    durationDays: q.durationDays,
    providerRateId: q.id,
    providerShipmentId: q.shipmentId,
  };
}

/**
 * Map an error code → buyer-facing copy. Exhaustive switch (type-driven §3
 * `never`-probe) — extending `ShippingRatesErrorCode` forces a compile-time
 * decision here.
 *
 * T13 (2026-07-23, `.tondocs/shippo/TEST_PLAN.md` §7 / `PLAN.md` WS4.9): the
 * matrix below is now the ONLY output of this function — every branch
 * returns a translated string, none return `state.message` (the raw route
 * string) verbatim anymore. The previous "existing codes fall through to
 * `state.message`" arm was the T13 gap: those messages ARE already
 * human-readable English written for an operator/reviewer reading logs, not
 * copy reviewed for buyer tone or translated for the other 8 locales.
 *
 * Grouping rationale:
 *  - `NO_RATES_TO_DESTINATION` / `OUT_OF_ORIGIN` — unchanged, pre-existing
 *    dedicated copy (PLAN §A "Checkout fallback" / WS8.1 origin gate).
 *  - `MISSING_FROM_ADDRESS` — seller never set a ship-from address. Buyer
 *    cannot fix this; copy points at "try later / contact seller".
 *  - `MISSING_PARCEL_DIMS` / `PARCEL_BELOW_CARRIER_MINIMUM` — both are the
 *    listing's parcel config being wrong (missing dims vs below the
 *    cross-carrier floor per PARCEL_MIN_DIMS.md §5). Same buyer-facing
 *    consequence (seller must fix the listing) so they share one key
 *    (`errorParcelConfig`) rather than forking near-identical copy.
 *  - `INVALID_ADDRESS` — the buyer's OWN address failed carrier validation;
 *    distinct from `VALIDATION_ERROR` (malformed request shape — a client
 *    bug, not something re-typing the address fixes) so it gets its own key
 *    (`errorInvalidAddress`) while `VALIDATION_ERROR` falls into the generic
 *    bucket below.
 *  - `SHIPPING_SANCTIONED_DESTINATION` — OFAC gate (route §O.2.7). Neutral,
 *    factual wording ("can't ship to this destination") — never the word
 *    "sanctioned" to the buyer.
 *  - `NETWORK_ERROR` — client-only code; connectivity retry copy.
 *  - `UNAUTHORIZED` / `MULTI_SELLER_FORBIDDEN` / `VALIDATION_ERROR` /
 *    `PROVIDER_ERROR` / `INTERNAL_ERROR` / `RESPONSE_SHAPE_INVALID` — no
 *    actionable buyer-facing distinction between these (auth glitch, request
 *    shape bug, carrier outage, our bug, contract drift); all map to the
 *    generic `errorGeneric` key (previously defined but dead — this is the
 *    first branch to actually use it).
 */
function deriveErrorCopy(
  state: Extract<ShippingRatesState, { kind: "error" }>,
  t: (key: string, values?: Record<string, string>) => string,
): string {
  const code: ShippingRatesErrorCode = state.code;
  switch (code) {
    case "NO_RATES_TO_DESTINATION": {
      // PLAN §A "Checkout fallback" copy — no silent legacy substitution.
      const country =
        state.details?.code === "NO_RATES_TO_DESTINATION"
          ? state.details.country
          : null;
      return country
        ? t("errorNoRatesToDestination", { country })
        : t("errorNoRatesToDestinationGeneric");
    }
    case "OUT_OF_ORIGIN": {
      // Defensive: WS8.1 eligibility gate normally prevents this from
      // reaching a buyer. Pre-gate listings or a seller who moved countries
      // post-listing can still hit it — surface a clear "contact the seller"
      // message rather than the technical reason.
      return t("errorOutOfOrigin");
    }
    case "MISSING_FROM_ADDRESS":
      return t("errorMissingFromAddress");
    case "MISSING_PARCEL_DIMS":
    case "PARCEL_BELOW_CARRIER_MINIMUM":
      return t("errorParcelConfig");
    case "INVALID_ADDRESS":
      return t("errorInvalidAddress");
    case "SHIPPING_SANCTIONED_DESTINATION":
      return t("errorSanctionedDestination");
    case "NETWORK_ERROR":
      return t("errorNetwork");
    case "UNAUTHORIZED":
    case "MULTI_SELLER_FORBIDDEN":
    case "VALIDATION_ERROR":
    case "PROVIDER_ERROR":
    case "INTERNAL_ERROR":
    case "RESPONSE_SHAPE_INVALID":
      return t("errorGeneric");
    default: {
      // Exhaustiveness probe — extending `ShippingRatesErrorCode` lights this
      // up at compile time. Memory: feedback_left_fix_audit_downstream_branches.
      const _exhaustive: never = code;
      void _exhaustive;
      return t("errorGeneric");
    }
  }
}

function deriveRatesView(
  state: ShippingRatesState,
  selection: ShippingRateSelection | null,
  t: (key: string, values?: Record<string, string>) => string,
): PickerView {
  switch (state.kind) {
    case "idle":
    case "loading":
      return {
        display: "loading",
        rates: [],
        selectedId: null,
        errorMessage: undefined,
        selection: null,
      };
    case "loaded":
      return {
        display: "loaded",
        rates: state.rates.map(quoteToRate),
        selectedId: state.selectedId,
        errorMessage: undefined,
        selection,
      };
    case "error":
      return {
        display: "error",
        rates: [],
        selectedId: null,
        errorMessage: deriveErrorCopy(state, t),
        selection: null,
      };
    default:
      return unreachable(state);
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LiveRateSubflow({
  sellerId,
  parcel,
  address,
  onComplete,
  onCancel,
  onSelectionChange,
  className,
}: LiveRateSubflowProps): React.ReactElement {
  const t = useTranslations("shippoCheckout");
  const { state, selection, fetchRates, selectRate } = useShippingRates();

  // Single-fire guard — concurrency-design §7 ack-once. Re-armed each time the
  // address changes (a fresh confirmation is legitimate after edits upstream).
  const completionFiredRef = React.useRef(false);

  // Deep-equality key over the input triple. When the upstream ShippingForm
  // edits any field, `addressKey` flips and the effect refires through the
  // race-safe hook (latest-wins + AbortController).
  const addressKey = React.useMemo(
    () => JSON.stringify({ sellerId, address, parcel }),
    [sellerId, address, parcel],
  );

  React.useEffect(() => {
    completionFiredRef.current = false;
    fetchRates({ sellerId, address, parcel });
    // addressKey is a deep snapshot of every input we care about; explicit dep
    // beats react-hooks/exhaustive-deps demanding the unstable object refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressKey, fetchRates]);

  const view = deriveRatesView(state, selection, t);
  const canContinue = view.selection !== null && !completionFiredRef.current;

  // Latest-ref to avoid retriggering the notify-effect on parent re-renders
  // that change the callback identity (react-state-design §10 stale closures).
  const onSelectionChangeRef = React.useRef(onSelectionChange);
  React.useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  }, [onSelectionChange]);
  React.useEffect(() => {
    onSelectionChangeRef.current?.(selection);
  }, [selection]);

  const handleContinue = React.useCallback(() => {
    if (completionFiredRef.current) return;
    if (!selection) return;
    completionFiredRef.current = true;
    onComplete(selection);
  }, [selection, onComplete]);

  return (
    <section
      className={cn("flex w-full min-w-0 flex-col gap-6", className)}
      aria-labelledby="live-rate-subflow-rates-title"
    >
      <header className="flex flex-col gap-1">
        <h2
          id="live-rate-subflow-rates-title"
          className="text-lg font-semibold"
        >
          {t("ratesTitle")}
        </h2>
        <p className="text-base text-muted-foreground">
          {t("ratesDescription")}
        </p>
      </header>

      <RatePicker
        rates={view.rates}
        selectedId={view.selectedId}
        onSelect={selectRate}
        display={view.display}
        {...(view.errorMessage !== undefined
          ? { errorMessage: view.errorMessage }
          : {})}
      />

      {/*
        Estimate-vs-truth disclosure (HANDOFF_RELIABILITY_GAPS_2026_06_27
        §"PR-G шаг 3"). When the seller's ship-from country differs from
        the buyer's destination, the checkout rate is an ESTIMATE — the
        ready-to-ship re-rate with customs is the source of truth. The
        §I.2 delta-cap (`lib/shippo/ready-to-ship.ts::decideDeltaCap`)
        bounds the buyer-charged vs label-bought delta to 5%; over-cap
        re-rates land on operator escalate (5–20%) or refund signal
        (>20%), never silently over-charge the buyer at print time.
        Pinned by `tests/integration/live-rate-intl-delta-cap.test.ts`.

        Both sides are server-truth: `fromCountry` from the rates
        endpoint (resolved seller `from_address`), `address.country`
        from the upstream `ShippingForm`. We normalise to upper-case
        on read because the rates contract pins UPPER-CASE on the wire,
        while the upstream form may emit lower-case for non-US/CA buyers.
      */}
      {state.kind === "loaded" &&
      state.fromCountry !== (address.country ?? "").toUpperCase() ? (
        <p
          className="text-sm text-muted-foreground"
          data-testid="live-rate-intl-disclosure"
        >
          {t("intlDisclosure")}
        </p>
      ) : null}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
        {onCancel ? (
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            className="text-base"
          >
            {t("actionCancel")}
          </Button>
        ) : null}
        {view.display !== "error" ? (
          <Button
            type="button"
            onClick={handleContinue}
            disabled={!canContinue}
            className="text-base"
          >
            {t("actionContinue")}
          </Button>
        ) : null}
      </div>
    </section>
  );
}
